/*
 * ca-geo-redirect.js
 * ------------------------------------------------------------------
 * Canada geo-detection — progressive enhancement only.
 *
 * We do not ship to Canada. For Canadian visitors we:
 *   1. Show a dismissible site-wide banner pointing at Amazon.ca.
 *   2. On PDPs, swap the Add-to-Cart / buy button for a
 *      "Buy on Amazon.ca" link (product custom.amazon_ca_url metafield,
 *      falling back to the Teeccino Amazon.ca brand search).
 *
 * Detection uses Cloudflare's already-live trace endpoint on our own
 * domain (no third-party geo-IP service). The loc= field is the
 * visitor's country. Result is cached in sessionStorage so we hit the
 * network at most once per session.
 *
 * SAFETY: everything here is additive. If the trace fetch fails, is
 * slow, or is blocked, the page behaves 100% normally for everyone.
 * Non-CA visitors get zero DOM changes.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Same-origin on the storefront domain; absolute (CORS: *) elsewhere, e.g. the
  // myshopify / theme-preview hosts. NOTE (2026-10-05): www.teeccino.com/cdn-cgi/trace
  // now 404s (www 301s to the apex), which silently disabled the July build.
  var TRACE_URL = /(^|\.)teeccino\.com$/.test(window.location.hostname)
    ? '/cdn-cgi/trace'
    : 'https://teeccino.com/cdn-cgi/trace';
  var SS_LOC_KEY = 'tc_geo_loc';               // sessionStorage: cached country code
  var BANNER_DISMISS_KEY = 'tc_ca_banner_dismissed'; // localStorage: persists dismiss
  var FALLBACK_AMAZON = 'https://www.amazon.ca/s?k=Teeccino';

  function safeGet(store, key) {
    try { return store.getItem(key); } catch (e) { return null; }
  }
  function safeSet(store, key, val) {
    try { store.setItem(key, val); } catch (e) { /* private mode / disabled */ }
  }

  function config() {
    return document.getElementById('ca-geo-config');
  }

  /* ---------------------------------------------------------------- */
  /* Amazon.ca URL construction — THE ONE PLACE                        */
  /* ---------------------------------------------------------------- */
  // Every Amazon.ca destination (banner, PDP button, card Quick Add) is built
  // here. decorateAmazonUrl() appends the Amazon Attribution tag (spec:
  // clawd-agentsmith/handoff/amazon-ca-tracking/attribution-tag-spec.md,
  // AmazonSmith 2026-10-19). The Liquid banner href in snippets/ca-geo-data.liquid
  // carries the same tag (surface=banner) so the no-JS href matches.
  var AA_TAG_PREFIX = 'maas=maas_adg_api_577303580473360972_static_9_793&ref_=aa_maas&tag=maas&aa_campaignid=tcom_geo_ca&aa_adgroupid=';
  var AA_TAG_SUFFIX = '&aa_creativeid=v1';
  var AA_SURFACES = { banner: 1, pdp: 1, card: 1 };
  function searchUrl() {
    var cfg = config();
    return (cfg && cfg.getAttribute('data-amazon-search')) || FALLBACK_AMAZON;
  }
  // surface: banner | pdp | card. JOIN rule: "?"+TAG if the URL has no "?",
  // else "&"+TAG. Never double-append (URL already carrying maas= is returned
  // untouched). Any #fragment is kept at the end.
  function decorateAmazonUrl(url, surface) {
    if (!url || url.indexOf('maas=') !== -1 || !AA_SURFACES[surface]) return url;
    var hash = '';
    var h = url.indexOf('#');
    if (h !== -1) { hash = url.slice(h); url = url.slice(0, h); }
    var tag = AA_TAG_PREFIX + surface + AA_TAG_SUFFIX;
    var last = url.charAt(url.length - 1);
    var sep = url.indexOf('?') === -1 ? '?' : (last === '?' || last === '&' ? '' : '&');
    return url + sep + tag + hash;
  }
  // productUrl: the product's custom.amazon_ca_url (may be empty).
  // Returns { url, type, tagged } where type is 'product' (metafield) or 'search' (fallback).
  function resolveAmazon(productUrl, surface) {
    var p = (productUrl || '').trim();
    var type = p ? 'product' : 'search';
    var url = decorateAmazonUrl(p || searchUrl(), surface);
    return { url: url, type: type, tagged: url.indexOf('maas=') !== -1 };
  }

  /* ---------------------------------------------------------------- */
  /* Click tracking — event `amazon_ca_click`                          */
  /* ---------------------------------------------------------------- */
  // Sinks (all fire-and-forget, synchronous, never throw, never block nav):
  //   1. Shopify.analytics.publish('amazon_ca_click', …)  -> Customer events.
  //      Reaches GA4 ONLY via a Custom Pixel that analytics.subscribe()s to it
  //      (the Google & YouTube app pixel forwards standard events only).
  //   2. dataLayer.push({ event: 'amazon_ca_click', … })   -> GTM, if ever restored.
  //      Deliberately NOT gtag('event'): the page-level gtag is an orphan stub
  //      (no gtag.js loader), and a gtag call would double-count if one returns.
  //   3. DOM CustomEvent 'teeccino:amazon_ca_click' on document.
  var SS_GEO_SRC_KEY = 'tc_geo_src'; // 'override' when ?tc_geo= pinned the country (QA)

  function trackAmazonClick(surface, dest, handle, title) {
    var cfg = config();
    var data = {
      surface: surface,                                  // banner | pdp | card
      destination_type: dest.type,                       // product | search
      destination_url: dest.url,                         // final, attribution-tagged URL
      tagged: !!dest.tagged,
      product_handle: handle || '',
      product_title: title || '',
      page_type: (cfg && cfg.getAttribute('data-template')) || '',
      geo_source: safeGet(window.sessionStorage, SS_GEO_SRC_KEY) === 'override' ? 'override' : 'trace'
    };
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('amazon_ca_click', data);
      }
    } catch (e) { /* never block navigation */ }
    try {
      window.dataLayer = window.dataLayer || [];
      var dl = { event: 'amazon_ca_click' };
      for (var k in data) { if (Object.prototype.hasOwnProperty.call(data, k)) dl[k] = data[k]; }
      window.dataLayer.push(dl);
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('teeccino:amazon_ca_click', { bubbles: true, detail: data }));
    } catch (e) {}
  }

  function pdpContext() {
    var cfg = config();
    return {
      handle: cfg ? (cfg.getAttribute('data-product-handle') || '') : '',
      title: cfg ? (cfg.getAttribute('data-product-title') || '') : ''
    };
  }

  /* ---------------------------------------------------------------- */
  /* Banner                                                           */
  /* ---------------------------------------------------------------- */
  function initBanner() {
    var banner = document.getElementById('ca-geo-banner');
    if (!banner) return;
    if (safeGet(window.localStorage, BANNER_DISMISS_KEY) === '1') return;

    var link = banner.querySelector('.ca-geo-banner__link');
    if (link) {
      // Banner always goes to the brand search (one URL builder for all surfaces).
      var bannerDest = resolveAmazon('', 'banner');
      link.href = bannerDest.url;
      var onBannerClick = function (e) {
        if (e.type === 'auxclick' && e.button !== 1) return; // middle-click only
        var ctx = pdpContext();
        trackAmazonClick('banner', bannerDest, ctx.handle, ctx.title);
      };
      link.addEventListener('click', onBannerClick);
      link.addEventListener('auxclick', onBannerClick);
    }

    banner.hidden = false;
    // Next frame so the [hidden]->visible transition actually animates.
    window.requestAnimationFrame(function () {
      banner.classList.add('ca-geo-banner--visible');
      document.body.classList.add('ca-geo-banner-active');
    });

    var dismiss = banner.querySelector('[data-ca-geo-dismiss]');
    if (dismiss) {
      dismiss.addEventListener('click', function () {
        banner.classList.remove('ca-geo-banner--visible');
        document.body.classList.remove('ca-geo-banner-active');
        safeSet(window.localStorage, BANNER_DISMISS_KEY, '1');
        window.setTimeout(function () { banner.hidden = true; }, 350);
      });
    }
  }

  /* ---------------------------------------------------------------- */
  /* PDP buy-button swap                                              */
  /* ---------------------------------------------------------------- */
  function pdpDestination() {
    var cfg = config();
    return resolveAmazon(cfg ? cfg.getAttribute('data-amazon-ca-url') : '', 'pdp');
  }

  function buildAmazonButton(sourceBtn, url) {
    var a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer nofollow';
    // Inherit the native button's classes so it matches the theme exactly,
    // then add our own hook for link-specific tweaks.
    a.className = (sourceBtn.className || '') + ' ca-geo-amazon-btn';
    a.setAttribute('data-ca-geo-amazon', '1');
    a.innerHTML =
      '<span class="ca-geo-amazon-btn__label">Buy on Amazon.ca</span>' +
      '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M6 3h7v7"></path><path d="M13 3 3.5 12.5"></path></svg>';
    return a;
  }

  function swapBuyButtons() {
    var cfg = config();
    if (!cfg || cfg.getAttribute('data-template') !== 'product') return;

    var url = pdpDestination().url;
    var buttons = document.querySelectorAll('[data-atc-button], .product-form__submit');

    Array.prototype.forEach.call(buttons, function (btn) {
      if (btn.getAttribute('data-ca-geo-swapped')) return;
      btn.setAttribute('data-ca-geo-swapped', '1');

      var link = buildAmazonButton(btn, url);
      // Hide the native ATC without removing it (keeps the <form> intact).
      btn.style.display = 'none';
      btn.setAttribute('aria-hidden', 'true');
      btn.tabIndex = -1;
      if (btn.parentNode) {
        btn.parentNode.insertBefore(link, btn.nextSibling);
      }
    });

    // Express / dynamic checkout buttons can't fulfil to Canada — hide them.
    var express = document.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]');
    Array.prototype.forEach.call(express, function (el) { el.style.display = 'none'; });
  }

  /* ---------------------------------------------------------------- */
  /* Detection                                                        */
  /* ---------------------------------------------------------------- */
  /* ---------------------------------------------------------------- */
  /* Collection-card "Quick Add" -> Amazon.ca                          */
  /* ---------------------------------------------------------------- */
  // Shop All / collection cards (snippets/shop-all-card.liquid) carry a
  // [data-quick-add] button that opens the QuickView add-to-cart modal
  // (assets/quickview.js, delegated bubble-phase click). For CA visitors we
  // relabel it and intercept the click in the CAPTURE phase on window, so the
  // modal never opens; the click goes to the product's Amazon.ca listing
  // (data-amazon-ca-url from custom.amazon_ca_url) or the brand search.
  var isCanada = false;
  var EXT_ICON =
    '<svg class="shopall-card__quick-add-icon" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M6 3h7v7"></path><path d="M13 3 3.5 12.5"></path></svg>';

  function cardDestination(btn) {
    return resolveAmazon(btn.getAttribute('data-amazon-ca-url'), 'card');
  }

  function swapQuickAdds() {
    var btns = document.querySelectorAll('[data-quick-add]:not([data-ca-geo-swapped])');
    Array.prototype.forEach.call(btns, function (btn) {
      btn.setAttribute('data-ca-geo-swapped', '1');
      btn.removeAttribute('aria-haspopup');
      btn.classList.add('ca-geo-quick-add');
      var title = btn.getAttribute('data-quick-add-title') || '';
      btn.setAttribute('aria-label', 'Buy ' + title + ' on Amazon.ca (opens in a new tab)');
      btn.innerHTML = EXT_ICON + '<span class="shopall-card__quick-add-label"><span class="ca-geo-quick-add__prefix">Buy on </span>Amazon.ca</span>';
    });
  }

  function onQuickAddClick(e) {
    if (!isCanada || !e.target || !e.target.closest) return;
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var dest = cardDestination(btn);
    // Track BEFORE window.open (synchronous, fire-and-forget) — this click is
    // preventDefault'd, so GA4 outbound-click measurement never sees it.
    trackAmazonClick('card', dest,
      btn.getAttribute('data-quick-add-handle') || '',
      btn.getAttribute('data-quick-add-title') || '');
    window.open(dest.url, '_blank', 'noopener');
  }
  window.addEventListener('click', onQuickAddClick, true);

  // PDP "Buy on Amazon.ca" links are rebuilt on every variant re-render, so
  // track them with one delegated listener instead of per-node handlers.
  // Native <a target=_blank> navigation is left untouched.
  function onPdpAmazonClick(e) {
    if (!isCanada || !e.target || !e.target.closest) return;
    if (e.type === 'auxclick' && e.button !== 1) return;
    var a = e.target.closest('a[data-ca-geo-amazon]');
    if (!a) return;
    var ctx = pdpContext();
    trackAmazonClick('pdp', pdpDestination(), ctx.handle, ctx.title);
  }
  document.addEventListener('click', onPdpAmazonClick);
  document.addEventListener('auxclick', onPdpAmazonClick);

  function applyCanada() {
    isCanada = true;
    initBanner();
    swapBuyButtons();
    swapQuickAdds();
    watchForRerenders();
  }

  // The PDP hero re-renders through the Section Rendering API on every variant
  // change (assets/pdp-hero.js: hero.innerHTML = newHero.innerHTML), which brings
  // back a fresh, un-swapped ATC button. Re-apply the swap whenever that happens.
  var observing = false;
  function watchForRerenders() {
    if (observing || typeof window.MutationObserver !== 'function') return;
    observing = true;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        if (document.querySelector('[data-atc-button]:not([data-ca-geo-swapped]), .product-form__submit:not([data-ca-geo-swapped])')) {
          swapBuyButtons();
        }
        // Cards re-render on filter / sort / load-more.
        if (document.querySelector('[data-quick-add]:not([data-ca-geo-swapped])')) {
          swapQuickAdds();
        }
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function handleLoc(loc) {
    if (loc === 'CA') applyCanada();
  }

  function parseTrace(text) {
    var loc = '';
    text.trim().split('\n').forEach(function (line) {
      var i = line.indexOf('=');
      if (i > -1 && line.slice(0, i) === 'loc') {
        loc = line.slice(i + 1).trim();
      }
    });
    return loc;
  }

  function detect() {
    // QA override: ?tc_geo=CA (or US, etc.) pins the country for this session so
    // the Canada experience can be reviewed from anywhere. Only affects the
    // visitor who uses it.
    var m = /[?&]tc_geo=([A-Za-z]{2})(?:&|$)/.exec(window.location.search);
    if (m) {
      safeSet(window.sessionStorage, SS_LOC_KEY, m[1].toUpperCase());
      safeSet(window.sessionStorage, SS_GEO_SRC_KEY, 'override');
      if (m[1].toUpperCase() === 'CA') window.localStorage && safeSet(window.localStorage, BANNER_DISMISS_KEY, '0');
    }

    var cached = safeGet(window.sessionStorage, SS_LOC_KEY);
    if (cached !== null && cached !== '') {
      handleLoc(cached);
      return;
    }

    if (typeof window.fetch !== 'function') return; // ancient browser: behave normally

    window.fetch(TRACE_URL, { cache: 'no-store', credentials: 'omit' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('bad status')); })
      .then(function (text) {
        var loc = parseTrace(text);
        safeSet(window.sessionStorage, SS_LOC_KEY, loc || 'unknown');
        handleLoc(loc);
      })
      .catch(function () { /* fail silent — page stays normal for everyone */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', detect);
  } else {
    detect();
  }
})();
